import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { Link } from '@xing-com/crate-core-router';
import { XING_JOBS_SEARCH_KI_PATH } from '@xing-com/crate-core-routes';
import { useAnimatedPlaceholder } from '@xing-com/crate-jobs-hooks';
import { IconAISearch } from '@xing-com/icons';
import { mediaConfined, mediaTopBarVisible } from '@xing-com/layout-tokens';
import { InputBar } from '@xing-com/text-field';
import { scale080, scale090, spaceM, spaceL, spaceXXL } from '@xing-com/tokens';

import { Promotions } from './promotions';

type Props = {
  withPromotions?: boolean;
  showPromotionsAsButtons?: boolean;
  withTitle: boolean;
};

export const FakeInput: React.FC<Props> = ({
  withPromotions,
  showPromotionsAsButtons,
  withTitle,
}) => {
  const { formatMessage } = useIntl();

  const inputRef = useRef<HTMLInputElement>(null);
  const runPlaceholderAnimation = useAnimatedPlaceholder(inputRef);

  useEffect(() => {
    runPlaceholderAnimation();
    // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Container $withTitle={withTitle}>
        <StyledLink
          to={XING_JOBS_SEARCH_KI_PATH}
          aria-label={formatMessage({
            id: 'SEARCH_AND_SWIPE_SEARCH_WITH_AI',
          })}
        >
          <Input
            icon={IconAISearch}
            size="xlarge"
            variant="plain"
            value=""
            ref={inputRef}
            aria-hidden="true"
            tabIndex={-1}
            data-testid="search-bar-fake-input"
          />
        </StyledLink>
      </Container>
      {withPromotions ? (
        <Promotions showPromotionsAsButtons={showPromotionsAsButtons} />
      ) : null}
    </>
  );
};

const StyledLink = styled(Link)`
  display: block;
  margin: 0 auto;
  padding: 0 ${spaceL};
  max-width: 648px;

  &:hover,
  &:focus {
    text-decoration: none;
  }

  @media ${mediaConfined} {
    padding: 0;
  }
`;

const Container = styled.div<{
  $withTitle?: boolean;
}>`
  @media ${mediaTopBarVisible} {
    padding-top: ${spaceM};
  }

  ${({ $withTitle }) =>
    $withTitle === false &&
    css`
      padding-top: ${spaceXXL};
    `};
`;

const Input = styled(InputBar)`
  & input {
    cursor: pointer;
    transition: border 0.18s ease-in-out;

    &::placeholder {
      font-size: ${scale080};
    }

    @media ${mediaConfined} {
      border-radius: 45px;

      &::placeholder {
        font-size: ${scale090};
      }
    }
  }
`;
